import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { EBookerLocationType } from '@/data/bookerData'

const bookerService = useBookerService()
export interface IBookerStoreState {
	booker: IBookerState
}

export interface IBookerStore {
	readonly state: IBookerStoreState
	setBooker(state: IBookerState): void
	setIsStartDateMenuOpen(isOpen: boolean): void
	setDates(start: Date, end: Date): void
	setIsRenteonDateError(isError: boolean): void
	setDeliveryCosts(type: EBookerLocationType, cost: number | null): void
	setSelectedLocations(start?: ILocationModel, end?: ILocationModel): void
	setIsRedirectToReservationAfterDate(redirect: boolean): void
	getBooker(): IBookerState
	getBookerStartLocation: ComputedRef<null | ILocationModel>
	getBookerDates: ComputedRef<{ start: Date; end: Date }>
	getIsRenteonDateError: ComputedRef<boolean | null>
	getIsOtherEndLocation: ComputedRef<boolean | null>
	getDeliveryCosts: ComputedRef<{
		start: number | null
		end: number | null
	}>
	getIsRedirectToReservationAfterDate: ComputedRef<boolean>
}

export const useBookerStore = defineStore('bookerStore', (): IBookerStore => {
	const _state = reactive<IBookerStoreState>({
		booker: {
			isStartLocationMenuOpen: false,
			isEndLocationMenuOpen: false,
			isStartDateMenuOpen: false,
			isRenteonDateError: false,
			otherEndLocation: false,
			selectedDates: {
				start: bookerService.getStartDate(),
				end: bookerService.getEndDate(),
			},
			selectedLocations: {
				start: null,
				end: null,
			},
			deliveryCosts: {
				start: null,
				end: null,
			},
			isRedirectToReservationAfterDate: false,
		},
	})

	const setBooker = (state: IBookerState) => {
		_state.booker = state
	}

	const setDates = (start: Date, end: Date) => {
		_state.booker.selectedDates.start = start
		_state.booker.selectedDates.end = end
	}

	const getBooker = () => {
		return _state.booker
	}

	const setIsStartDateMenuOpen = (isOpen: boolean) => {
		_state.booker.isStartDateMenuOpen = isOpen
	}

	const setIsRenteonDateError = (isError: boolean) => {
		_state.booker.isRenteonDateError = isError
	}

	const setDeliveryCosts = (type: EBookerLocationType, cost: number | null) => {
		_state.booker.deliveryCosts[type] = cost
	}

	const setSelectedLocations = (start?: ILocationModel, end?: ILocationModel) => {
		if (start) {
			_state.booker.selectedLocations.start = start
		}
		if (end) {
			_state.booker.selectedLocations.end = end
		}
	}

	const setIsRedirectToReservationAfterDate = (redirect: boolean) => {
		_state.booker.isRedirectToReservationAfterDate = redirect
	}

	const getIsRenteonDateError = computed(() => {
		return _state.booker.isRenteonDateError
	})

	const getBookerStartLocation = computed(() => {
		return _state.booker.selectedLocations.start
	})

	const getBookerDates = computed(() => {
		return _state.booker.selectedDates
	})

	const getDeliveryCosts = computed(() => {
		return _state.booker.deliveryCosts
	})

	const getIsOtherEndLocation = computed(() => {
		return _state.booker.otherEndLocation
	})

	const getIsRedirectToReservationAfterDate = computed(() => {
		return _state.booker.isRedirectToReservationAfterDate
	})

	return {
		state: _state,
		setBooker,
		setIsStartDateMenuOpen,
		setIsRenteonDateError,
		setDates,
		setDeliveryCosts,
		setSelectedLocations,
		setIsRedirectToReservationAfterDate,
		getBooker,
		getBookerDates,
		getIsRenteonDateError,
		getBookerStartLocation,
		getDeliveryCosts,
		getIsOtherEndLocation,
		getIsRedirectToReservationAfterDate,
	}
})
